import { FieldProps, FormikProps, getIn } from 'formik';
import _ from 'lodash';
import * as React from 'react';
import { OnChangeValue } from 'react-select';
import { AsyncSelect, IAsyncSelectProps } from '../../select/async';
import { ISelectOption } from '../../select/select';
// import { AsyncSelect, IAsyncSelectProps, ISelectOption } from '../../';
import * as S from './async-select.styles';

type IAsyncSelectFieldProps = IAsyncSelectProps &
    FieldProps & {
        loadOptions: (inputValue: string) => Promise<ISelectOption[]>;
        onChange?: (option: OnChangeValue<ISelectOption, boolean>, form: FormikProps<any>) => Promise<void> | void;
        getDefault?: (id: string, form: FormikProps<any>) => Promise<ISelectOption | null>;
        postfixComponent?: React.ReactElement;
    };

/**
 * Wrapper around BaseSelect to be able to work with Form components
 */
export const AsyncSelectField: React.FunctionComponent<IAsyncSelectFieldProps> = (
    props: IAsyncSelectFieldProps,
): React.ReactElement => {
    const { field, form, postfixComponent, ...otherProps } = props;
    const error = getIn(form.errors, field.name);
    const touched = getIn(form.touched, field.name);
    const errorMessage: string = touched && error ? error : null;

    const [selectedValue, setSelectedValue] = React.useState<ISelectOption | null>(
        field.value && typeof field.value === 'object' ? field.value : null,
    );
    const [disabled, setDisabled] = React.useState(false);

    React.useEffect(() => {
        if (selectedValue?.value !== field.value) {
            updateSelectedValue(field.value);
        }
    }, [field.value]);

    const updateSelectedValue = async (value: string): Promise<void> => {
        if (!props.getDefault) {
            return;
        }

        if (!value) {
            setSelectedValue(null);
            return;
        }

        // Do not disable the select while resolving getDefault — toggling isDisabled
        // remounts/resets AsyncSelect option loading (defaultOptions + cacheOptions)
        // and can leave the menu empty even when the API returned data.
        try {
            const defaultValue = await props.getDefault(value, form);
            setSelectedValue(defaultValue ?? { label: String(value), value });
        } catch (e) {
            console.warn('AsyncSelectField getDefault failed', e);
            setSelectedValue({ label: String(value), value });
        }
    };

    const handleChange = React.useCallback(async (option: OnChangeValue<ISelectOption, boolean>) => {
        // Handle clear (when option is null/undefined)
        if (!option) {
            setSelectedValue(null);
            setDisabled(true);
            try {
                await props.onChange?.(null, form);
            } finally {
                setDisabled(false);
            }
            form.setFieldValue(field.name, null);
            form.setFieldTouched(field.name, true, false);
            return;
        }

        const selectedOption = option as ISelectOption;

        setSelectedValue(selectedOption);
        setDisabled(true);
        try {
            await props.onChange?.(selectedOption, form);
        } finally {
            setDisabled(false);
        }

        // In case of multiselect
        if (Array.isArray(option)) {
            form.setFieldValue(
                field.name,
                option.map(a => a.value),
            );
        } else {
            form.setFieldValue(field.name, _.get(option, 'value', null));
        }

        // Mark as touched since we use validateOnBlur=false
        form.setFieldTouched(field.name, true, false);
    }, []);

    const postfix = _.isFunction(postfixComponent) ? postfixComponent(selectedValue) : postfixComponent;

    return (
        <S.SelectContainer>
            <AsyncSelect
                {...otherProps}
                {...field}
                // AsyncSelect maps the `disabled` prop to react-select's isDisabled
                disabled={disabled || props.disabled || props.isDisabled}
                errorMessage={errorMessage}
                menuPosition="fixed"
                value={selectedValue}
                onChange={handleChange}
                defaultOptions={true}
                cacheOptions={true}
            />
            <S.PostfixContainer>{postfix}</S.PostfixContainer>
        </S.SelectContainer>
    );
};
